import { CancellationToken } from "../../../base/common/cancellation";
import { fuzzyScore } from "../../../base/common/filters";
import { FuzzyScorerCache, prepareQuery, scoreFuzzy2, scoreItemFuzzy } from "../../../base/common/fuzzyScorer";
import { DisposableStore } from "../../../base/common/lifecycle";
import { fuzzyContains } from "../../../base/common/strings";
import { IAssetsService } from "../../../editor/services/assets/common/assets";
import { IAnalyticsService } from "../../analytics/common/analytics";
import { ICommandService } from "../../commands/common/commands";
import { IHierarchyService } from "../../hierarchy/common/hierarchy";
import { ILogService } from "../../log/common/log";
import { isPropertyLike } from "../../properties/common/properties";
import { SetPropertyCommand } from "../../properties/common/propertyCommands";
import { pickAssetVersion } from "../../../editor/services/assets/common/assetsService";
import { FastAndSlowPicks, IPickerQuickAccessItem, PickerQuickAccessProvider, Picks } from "../../quickinput/browser/pickerAccessProvider";
import { IQuickAccessProviderRunOptions } from "../../quickinput/common/quickAccess";
import { IQuickPickItemWithResource, IQuickPickSeparator, quickPickItemScorerAccessor } from "../../quickinput/common/quickInput";
import { IResourceService } from "../../resources/common/resources";
import { IStorageService, StorageScope, StorageTarget } from "../../storage/common/storage";
import { VSBuffer } from "../../../base/common/buffer";
import { SetMaterialCommand } from "../../webgl/three.js/commands/materialCommands";
import { isMaterial, isTexture } from "../../webgl/three.js/helper";
import { isNodeMaterial } from "../../webgl/three.js/helper.nodes";
import { fuzzyScoreSortingHelper } from "./helper";
import { createResourceQuickPickPreviewTarget } from "./quickPickPreview.js";

interface IPropertyPickItem extends IQuickPickItemWithResource, IPickerQuickAccessItem {
    minScore?: number;
    sortOffset?: number;
}

type PropertyPickerItem = IPropertyPickItem | IQuickPickSeparator;

export class PropertyPicker extends PickerQuickAccessProvider<IPropertyPickItem> {

    static readonly PREFIX = "@property ";
    static readonly MATERIAL_PREFIX = "@property material ";
    static readonly TEXTURE_PREFIX = "@property texture ";
    static readonly HDRI_PREFIX = "@property hdri ";
    static readonly OBJECT_PREFIX = "@property object ";
    static readonly URL_PREFIX = "@property url ";

    private readonly fuzzyCache: FuzzyScorerCache = {};

    private static readonly NoResultItem: IPropertyPickItem = {
        id: `no-results`,
        label: `No results found`,
    }

    constructor(
        @ICommandService private readonly commandService: ICommandService,
        @IResourceService private readonly resourceCacheService: IResourceService,
        @IAnalyticsService private readonly analyticsService: IAnalyticsService,
        @IAssetsService private readonly assetsService: IAssetsService,
        @IStorageService private readonly storageService: IStorageService,
        @IHierarchyService private readonly hierarchyService: IHierarchyService,
        @ILogService logService: ILogService,
    ) {
        super(PropertyPicker.PREFIX);
    }

    private getStorageKey(filterPrefix: string): string {
        // Create separate storage keys for each filter type
        if (filterPrefix === "material") return "propertypicker.lastpicks.material";
        if (filterPrefix === "hdri" || filterPrefix === "hdr") return "propertypicker.lastpicks.hdri";
        if (filterPrefix === "texture") return "propertypicker.lastpicks.texture";
        if (filterPrefix === "hdri-texture") return "propertypicker.lastpicks.environment";
        if (filterPrefix === "object") return "propertypicker.lastpicks.object";
        if (filterPrefix === "url") return "propertypicker.lastpicks.url";
        return "propertypicker.lastpicks.default";
    }

    private addToLastPicks(item: IPropertyPickItem, filterPrefix: string) {
        if (!item?.id) return;
        const storageKey = this.getStorageKey(filterPrefix);
        const lastpicks = this.storageService.getObject<string[]>(storageKey, StorageScope.WORKSPACE, []).filter(e => typeof e === "string");
        const existingIndex = lastpicks.findIndex(pick => pick === item.id);
        if (existingIndex !== -1) {
            lastpicks.splice(existingIndex, 1);
        }
        lastpicks.unshift(item.id);
        if (lastpicks.length > 20) {
            lastpicks.pop();
        }
        this.storageService.store(storageKey, lastpicks, StorageScope.WORKSPACE, StorageTarget.USER);
    }


    protected async _getPicks(filter: string, disposables: DisposableStore, token: CancellationToken, runOptions?: IQuickAccessProviderRunOptions): Promise<Picks<IPropertyPickItem>> {

        const results: IPropertyPickItem[] = [];

        const target = runOptions?.target;
        const property = isPropertyLike(target) ? target : undefined;
        if (!property) {
            throw new Error("PropertyPicker: target is not a property");
        }

        // Parse filter to extract asset type hints
        // Note: "@property " prefix is already removed by PickerQuickAccessProvider
        const filterLower = filter.toLowerCase().trim();
        const filterTokens = filterLower.split(/\s+/).filter(Boolean);
        const assetTypeHints = {
            material: false,
            hdri: false,
            texture: false,
            object: false,
            url: false,
        };
        let searchTokenStartIndex = 0;
        for (const tokenText of filterTokens) {
            if (tokenText === "material") {
                assetTypeHints.material = true;
                searchTokenStartIndex++;
                continue;
            }
            if (tokenText === "hdri" || tokenText === "hdr") {
                assetTypeHints.hdri = true;
                searchTokenStartIndex++;
                continue;
            }
            if (tokenText === "texture") {
                assetTypeHints.texture = true;
                searchTokenStartIndex++;
                continue;
            }
            if (tokenText === "object") {
                assetTypeHints.object = true;
                searchTokenStartIndex++;
                continue;
            }
            if (tokenText === "url") {
                assetTypeHints.url = true;
                searchTokenStartIndex++;
                continue;
            }
            break;
        }

        // Determine filter prefix for storage key
        let filterPrefix = "default";
        if (assetTypeHints.hdri && assetTypeHints.texture) filterPrefix = "hdri-texture";
        else if (assetTypeHints.material) filterPrefix = "material";
        else if (assetTypeHints.hdri) filterPrefix = "hdri";
        else if (assetTypeHints.texture) filterPrefix = "texture";
        else if (assetTypeHints.object) filterPrefix = "object";
        else if (assetTypeHints.url) filterPrefix = "url";

        // Remove all leading type keywords from the search query
        const searchQuery = filterTokens.slice(searchTokenStartIndex).join(" ").trim();

        // Scene materials are registered by ThreeResourceTracker; we only consume here.

        // @TODO: use property accept metadata to narrow down resource types

        const query = prepareQuery(searchQuery);

        // SCENE OBJECTS for object-reference slots — the hierarchy IS the source
        // (same nodes the tree shows); picking assigns via SetPropertyCommand so
        // the edit rides the normal rail (undo/capture). Remote 3D ASSETS list
        // below them (Marcel 2026-07-31: assign a remote asset OR a scene object).
        if (assetTypeHints.object) {
            const stack = [...this.hierarchyService.roots];
            while (stack.length > 0) {
                if (token.isCancellationRequested) break;
                const node = stack.pop()!;
                stack.push(...node.children);
                const owner = node.owner as { isObject3D?: boolean; type?: string } | undefined;
                if (!owner?.isObject3D) continue;
                const label = node.name || node.label || owner.type || "Object";
                const parentName = node.parent?.name || node.parent?.label;
                const item: IPropertyPickItem = {
                    id: `object.${node.id}`,
                    name: label,
                    label,
                    description: [owner.type, parentName ? `in ${parentName}` : ""].filter(Boolean).join(", "),
                    icon: "deployed_code",
                    accept: () => {
                        this.addToLastPicks(item, filterPrefix);
                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: node.owner,
                            metadata: {
                                source: "user",
                                target: property.key
                            }
                        });
                    }
                };
                results.push(item);
            }

            // REMOTE 3D ASSETS (cloud catalog + providers): assign by URL — the
            // resolved default version's uri. Fields wanting an AssetReference
            // wrap the string in their owner setter (data-class array editor).
            const modelAssets = await this.assetsService.list({ type: "model" }, token);
            for (const asset of modelAssets.assets) {
                if (token.isCancellationRequested) break;
                const item: IPropertyPickItem = {
                    id: `object-asset.${asset.provider ?? ""}.${asset.id}`,
                    name: asset.name,
                    label: asset.name || asset.id,
                    description: asset.provider ? `Asset — ${asset.provider}` : "Asset",
                    icon: "asset",
                    thumbnailUrl: asset.thumbnailUrl,
                    tooltip: asset.thumbnailUrl ? `![thumbnail](${asset.thumbnailUrl})` : undefined,
                    sortOffset: -1000000, // scene objects first
                    accept: () => {
                        this.addToLastPicks(item, filterPrefix);
                        void (async () => {
                            const full = await this.assetsService.loadAsset(asset.id, undefined, asset.provider);
                            const version = full ? pickAssetVersion(full.versions) : undefined;
                            const url = version?.uri?.toString(true);
                            if (!url) return;
                            this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                                property: property,
                                value: url,
                                metadata: {
                                    source: "user",
                                    target: property.key
                                }
                            });
                        })();
                    }
                };
                results.push(item);
            }
        }

        // URL fields (image markers, …): the PROJECT's images + import-from-disk —
        // both assign the project-relative path; import rides THE asset-import
        // codepath (importAsset), the same seam a later cloud upload uses.
        if (assetTypeHints.url) {
            const importItem: IPropertyPickItem = {
                id: "url.import",
                label: "Import from disk…",
                description: "Copy a file into the project and assign it",
                icon: "folder",
                sortOffset: 1000000,
                accept: () => {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.onchange = async () => {
                        const file = input.files?.[0];
                        if (!file) return;
                        const data = VSBuffer.wrap(new Uint8Array(await file.arrayBuffer()));
                        const imported = await this.assetsService.importAsset({ source: "project", name: file.name, data });
                        if (!imported?.path) return;
                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: imported.path,
                            metadata: { source: "user", target: property.key }
                        });
                    };
                    input.click();
                }
            };
            results.push(importItem);
            const projectImages = await this.assetsService.list({ type: "image", source: "project" }, token);
            for (const asset of projectImages.assets) {
                if (token.isCancellationRequested) break;
                const path = asset.path ?? asset.id;
                // the image IS its preview — resolved through the provider's lazy
                // thumbnail API (data url, cached per path@mtime)
                const thumbnail = asset.thumbnailUrl ?? await this.assetsService.getAssetThumbnail?.(asset, token) ?? undefined;
                const item: IPropertyPickItem = {
                    id: `url.project.${asset.id}`,
                    name: asset.name,
                    label: asset.name || path,
                    description: path,
                    icon: "texture",
                    thumbnailUrl: thumbnail,
                    tooltip: thumbnail ? `![thumbnail](${thumbnail})` : undefined,
                    accept: () => {
                        this.addToLastPicks(item, filterPrefix);
                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: path,
                            metadata: { source: "user", target: property.key }
                        });
                    }
                };
                results.push(item);
            }
        }

        // Only surface local materials when the field actually wants them: an
        // explicit "material" hint, or no asset-type hint at all (default). A
        // texture/hdri field (e.g. a texture slot, or a graph texture node) would
        // otherwise list every scene material. The remote fetch below already
        // respects the hints; this loop did not.
        const wantMaterials = assetTypeHints.material || (!assetTypeHints.texture && !assetTypeHints.hdri && !assetTypeHints.object && !assetTypeHints.url);

        for (const { entry, resource: ref } of this.resourceCacheService.resources("material")) {
            if (token.isCancellationRequested) break;

            if (isMaterial(ref)) {
                if (!wantMaterials) continue;

                // We can not set the output color transform node material via picker
                if (isNodeMaterial(ref) && ref.name === "outputColorTransform") {
                    continue; // skip default node material
                }

                const label = ref.name || ref.type;
                let description = ref.type !== label ? ref.type : ref.type;
                if ((ref as any).wireframe === true) description += ", Wireframe";
                const id = `material.${ref.uuid}`;
                const item: IPropertyPickItem = {
                    id: id,
                    name: ref.name,
                    label: ref.name || (ref.type + "*"),
                    description: description,
                    icon: "material",
                    previewTarget: createResourceQuickPickPreviewTarget(entry),
                    accept: (keymods) => {
                        this.analyticsService.logEvent("picker", {
                            action: "pick",
                            from: "property",
                            type: ref.type,
                        });

                        this.addToLastPicks(item, filterPrefix);

                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: ref,
                            metadata: {
                                source: "user",
                                target: property.key
                            }
                        });
                    }
                };
                results.push(item);
            }
        }

        // Scene textures (from the resource registry) for texture/hdri slots —
        // available offline/logged-out, unlike the remote asset fetch below.
        const wantTextures = assetTypeHints.texture || assetTypeHints.hdri;
        if (wantTextures) {
            for (const { entry, resource: ref } of this.resourceCacheService.resources("texture")) {
                if (token.isCancellationRequested) break;
                if (!isTexture(ref)) continue;

                const label = ref.name || ref.constructor?.name || "Texture";
                const image = (ref as { image?: { width?: number; height?: number } }).image;
                const dimensions = image?.width && image?.height ? `${image.width}×${image.height}` : "";
                const typeName = ref.constructor?.name ?? "Texture";
                const description = ["Scene texture", typeName !== label ? typeName : "", dimensions]
                    .filter(Boolean)
                    .join(", ");
                const id = `texture.${ref.uuid}`;
                const item: IPropertyPickItem = {
                    id: id,
                    name: ref.name,
                    label: ref.name || (typeName + "*"),
                    description: description,
                    icon: "texture",
                    previewTarget: createResourceQuickPickPreviewTarget(entry),
                    accept: () => {
                        this.analyticsService.logEvent("picker", {
                            action: "pick",
                            from: "property",
                            type: typeName,
                        });

                        this.addToLastPicks(item, filterPrefix);

                        this.commandService.executeCommand<SetPropertyCommand>(SetPropertyCommand.ID, {
                            property: property,
                            value: ref,
                            metadata: {
                                source: "user",
                                target: property.key
                            }
                        });
                    }
                };
                results.push(item);
            }
        }

        if (token.isCancellationRequested) return [];

        // Determine which asset types to fetch based on the filter
        const assetTypesToFetch: Array<"material" | "hdr" | "texture"> = [];

        // If specific types are mentioned, only fetch those
        if (assetTypeHints.material || assetTypeHints.hdri || assetTypeHints.texture) {
            if (assetTypeHints.material) assetTypesToFetch.push("material");
            if (assetTypeHints.hdri) assetTypesToFetch.push("hdr");
            if (assetTypeHints.texture) assetTypesToFetch.push("texture");
        } else if (!assetTypeHints.object && !assetTypeHints.url) {
            // Default: fetch materials (existing behavior). Object slots list their
            // own asset set above; url slots list project files + import.
            assetTypesToFetch.push("material");
        }

        // Fetch assets for each type
        for (const assetType of assetTypesToFetch) {
            if (token.isCancellationRequested) break;

            const assets = await this.assetsService.list({ type: assetType }, token);
            for (const asset of assets.assets) {
                if (token.isCancellationRequested) break;
                const id = `asset.${asset.id}`;
                let description = asset.description && asset.description.length > 100 ? asset.description.substring(0, 100) + "..." : asset.description;
                if (asset.provider) description = asset.provider + (description ? ` – ${description}` : "");

                let tooltip: string | undefined = undefined;
                if (asset.thumbnailUrl) {
                    tooltip = `![thumbnail](${asset.thumbnailUrl})`;
                }

                // Update label based on asset type
                let label = asset.name || `Unnamed ${assetType}`;

                const item: IPropertyPickItem = {
                    id: id,
                    name: undefined,
                    label: label,
                    description: description,
                    icon: assetType === "hdr" ? "hdri" : "asset",
                    thumbnailUrl: asset.thumbnailUrl,
                    tooltip: tooltip,

                    // minScore: 120,
                    sortOffset: -1000000, // prefer local materials
                    accept: (keymods) => {
                        this.addToLastPicks(item, filterPrefix);
                        // @TODO: select does use current selection which is NOT what we want here
                        return this.assetsService.handleInteraction({
                            type: "targets",
                            assets: [asset],
                            targets: [property],
                        }, token)?.then(() => { })
                    }
                };
                results.push(item);
            }
        }

        // If there's no search query, show recent picks at the top
        if (!searchQuery || searchQuery.trim().length === 0) {
            const storageKey = this.getStorageKey(filterPrefix);
            const lastpicks = this.storageService.getObject<string[]>(storageKey, StorageScope.WORKSPACE, []).filter(e => typeof e === "string");

            if (lastpicks.length > 0) {
                // Find recent picks that exist in current results
                const recentItems = lastpicks
                    .map(pickId => results.find(item => item.id === pickId))
                    .filter((item): item is IPropertyPickItem => item !== undefined);

                if (recentItems.length > 0) {
                    // Get remaining items that weren't in recent picks
                    const recentIds = new Set(recentItems.map(item => item.id));
                    const remainingItems = results.filter(item => !recentIds.has(item.id));

                    // Sort remaining items
                    remainingItems.sort((a, b) => {
                        const score_a = (a.sortOffset || 0);
                        const score_b = (b.sortOffset || 0);
                        if (score_a !== score_b) {
                            return score_b - score_a; // Higher sortOffset first
                        }
                        return (a.label || "").localeCompare(b.label || "");
                    });

                    // Add separator between recent picks and other items (if there are remaining items)
                    if (remainingItems.length > 0) {
                        const separator: IQuickPickSeparator = {
                            type: 'separator'
                        };
                        // Return with separator: cast to any to work around type constraint
                        return [...recentItems, separator, ...remainingItems] as any;
                    }

                    // Only recent items, no separator needed
                    return recentItems;
                }
            }

            // No recent picks, return all sorted
            return results.sort((a, b) => {
                const score_a = (a.sortOffset || 0);
                const score_b = (b.sortOffset || 0);
                if (score_a !== score_b) {
                    return score_b - score_a; // Higher sortOffset first
                }
                return (a.label || "").localeCompare(b.label || "");
            });
        }

        const scoredResults = results
            .map(item => {
                const score = scoreItemFuzzy(item, query, true, quickPickItemScorerAccessor, this.fuzzyCache);
                return { item, score };
            })
            .filter(({ item, score }) => score.score > (item.minScore || 0))
            .sort((a, b) => {
                let score_a = a.score.score;
                let score_b = b.score.score;
                if (a.item.sortOffset) score_a += a.item.sortOffset;
                if (b.item.sortOffset) score_b += b.item.sortOffset;
                return fuzzyScoreSortingHelper(a.item.name, score_a, b.item.name, score_b);
            })
            .map(({ item, score }) => {
                item.label += " " + score.score;
                return item;
            });

        if (scoredResults.length <= 0) {
            return [PropertyPicker.NoResultItem];
        }
        return scoredResults;
    }

}
